import { useEffect, useState } from "react";
import { Mail, Phone, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { EMAIL, EMAIL2, PHONE_DISPLAY, PHONE_TEL, PHONE2_DISPLAY, PHONE2_TEL, WHATSAPP_URL, FORMSPREE_ENDPOINT } from "@/data/content";

const INIT = { name: "", email: "", phone: "", message: "" };

const clean = (s) => s.replace(/[<>]/g, "").trim();
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default function ContactForm({ divisionLabel, prefill }) {
  const [form, setForm] = useState(INIT);
  const [status, setStatus] = useState("idle");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (prefill) setForm((f) => ({ ...f, message: prefill }));
  }, [prefill]);

  const validate = () => {
    const e = {};
    if (clean(form.name).length < 2) e.name = "Please enter your name";
    if (!EMAIL_RE.test(clean(form.email))) e.email = "Please enter a valid email address";
    if (clean(form.phone).replace(/\D/g, "").length < 10) e.phone = "Please enter a valid phone number";
    if (clean(form.message).length < 5) e.message = "Please tell us a little about your requirement";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus("sending");

    if (FORMSPREE_ENDPOINT.includes("YOUR_FORM_ID")) {
      setStatus("idle");
      toast.warning("Our online form is being connected. Please reach us instantly on WhatsApp or call us meanwhile.", {
        action: { label: "WhatsApp", onClick: () => window.open(WHATSAPP_URL, "_blank") },
      });
      return;
    }

    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: clean(form.name),
          email: clean(form.email),
          phone: clean(form.phone),
          message: clean(form.message),
          division: divisionLabel,
        }),
      });
      if (!res.ok) throw new Error("Formspree error");
      setStatus("sent");
      setForm(INIT);
      toast.success("Enquiry sent. Our team will get back to you shortly.");
    } catch {
      setStatus("error");
      toast.error("Could not send right now. Please try WhatsApp or call us directly.");
    }
  };

  const inputCls =
    "w-full rounded-xl border border-slate-700 bg-white/[0.04] px-4 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none transition-colors duration-300 focus:border-brand focus:bg-white/[0.06]";

  return (
    <section id="contact" className="relative scroll-mt-24 overflow-hidden px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24" data-testid="contact-section">
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <img
          data-parallax-img
          src="https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=1800&auto=format&fit=crop"
          alt=""
          className="absolute -top-[12%] left-0 h-[124%] w-full object-cover opacity-[0.06]"
        />
      </div>
      <div className="relative mx-auto grid max-w-7xl gap-10 sm:gap-12 lg:grid-cols-[1fr_1.2fr] lg:gap-14">
        <div data-reveal>
          <p className="kicker mb-4">Contact</p>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Tell us what you want to secure, connect or automate
          </h2>
          <p className="mt-5 text-sm leading-relaxed text-slate-400 sm:text-base">
            Share a few details and our {divisionLabel.toLowerCase()} team will call you back with a
            solution designed around your needs — not a catalogue.
          </p>
          <div className="mt-9 space-y-4">
            <a
              data-testid="contact-email-link"
              href={`mailto:${EMAIL}`}
              className="group flex items-center gap-4 rounded-xl border border-slate-700/80 bg-slate-900/50 p-4 transition-colors duration-300 hover:border-brand/60"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand/15 text-brand">
                <Mail size={18} />
              </span>
              <span className="min-w-0">
                <span className="block text-[10px] font-tech uppercase tracking-[0.25em] text-slate-500">Email</span>
                <span className="block break-all text-sm font-medium text-white">{EMAIL}</span>
                <span className="block break-all text-sm font-medium text-white">{EMAIL2}</span>
              </span>
            </a>
            <a
              data-testid="contact-phone-link"
              href={`tel:${PHONE_TEL}`}
              className="group flex items-center gap-4 rounded-xl border border-slate-700/80 bg-slate-900/50 p-4 transition-colors duration-300 hover:border-brand/60"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand/15 text-brand">
                <Phone size={18} />
              </span>
              <span className="min-w-0">
                <span className="block text-[10px] font-tech uppercase tracking-[0.25em] text-slate-500">Phone / WhatsApp</span>
                <span className="block text-sm font-medium text-white">{PHONE_DISPLAY}</span>
                <span className="block text-sm font-medium text-white">{PHONE2_DISPLAY}</span>
              </span>
            </a>
          </div>
        </div>

        <form
          data-testid="contact-form"
          onSubmit={submit}
          data-reveal
          className="glass min-w-0 rounded-3xl p-5 sm:p-9"
          aria-label="Enquiry form"
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="cf-name" className="mb-2 block text-xs font-medium text-slate-300">Name</label>
              <input id="cf-name" data-testid="contact-form-name" value={form.name} onChange={set("name")} placeholder="Your full name" aria-label="Your name" aria-invalid={!!errors.name} className={inputCls} />
              {errors.name && <p className="mt-1.5 text-xs text-red-400" data-testid="contact-error-name">{errors.name}</p>}
            </div>
            <div>
              <label htmlFor="cf-email" className="mb-2 block text-xs font-medium text-slate-300">Email</label>
              <input id="cf-email" data-testid="contact-form-email" type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" aria-label="Your email" aria-invalid={!!errors.email} className={inputCls} />
              {errors.email && <p className="mt-1.5 text-xs text-red-400" data-testid="contact-error-email">{errors.email}</p>}
            </div>
          </div>
          <div className="mt-5">
            <label htmlFor="cf-phone" className="mb-2 block text-xs font-medium text-slate-300">Phone</label>
            <input id="cf-phone" data-testid="contact-form-phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="+91 ..." aria-label="Your phone number" aria-invalid={!!errors.phone} className={inputCls} />
            {errors.phone && <p className="mt-1.5 text-xs text-red-400" data-testid="contact-error-phone">{errors.phone}</p>}
          </div>
          <div className="mt-5">
            <label htmlFor="cf-message" className="mb-2 block text-xs font-medium text-slate-300">Message</label>
            <textarea id="cf-message" data-testid="contact-form-message" rows={5} value={form.message} onChange={set("message")} placeholder={`Tell us about your ${divisionLabel.toLowerCase()} requirement...`} aria-label="Your message" aria-invalid={!!errors.message} className={`${inputCls} resize-none`} />
            {errors.message && <p className="mt-1.5 text-xs text-red-400" data-testid="contact-error-message">{errors.message}</p>}
          </div>
          <button
            data-testid="contact-form-submit"
            type="submit"
            disabled={status === "sending"}
            className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-brand px-8 py-4 text-sm font-bold text-white transition-all duration-300 hover:bg-brand-hover hover:shadow-lg hover:shadow-brand/30 disabled:opacity-60"
          >
            {status === "sending" ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} />}
            {status === "sending" ? "Sending..." : "Submit Enquiry"}
          </button>
          <p className="mt-4 text-center text-[11px] leading-relaxed text-slate-500">
            Typically responded to within 12–24 hours · Your details stay private · No spam, ever
          </p>
          {status === "sent" && (
            <p data-testid="contact-form-success" className="mt-4 text-center text-xs text-emerald-400">
              Thank you — your enquiry has been received.
            </p>
          )}
          {status === "error" && (
            <p data-testid="contact-form-error" className="mt-4 text-center text-xs text-red-400">
              Something went wrong. Please use WhatsApp or call us directly.
            </p>
          )}
        </form>
      </div>
    </section>
  );
}
